import { memo } from "react";
import { Plus, Trash2 } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import type {
  RentalPayment,
  RentalPaymentMethod,
} from "../../types/rental.types";
import {
  INSTALLMENT_OPTIONS,
  MIN_INSTALLMENTS,
  supportsInstallments,
} from "../../constants";

const PAYMENT_METHOD_OPTIONS: {
  value: RentalPaymentMethod;
  label: string;
}[] = [
  { value: "PIX", label: "PIX" },
  { value: "CREDIT_CARD", label: "Crédito" },
  { value: "DEBIT_CARD", label: "Débito" },
  { value: "CASH", label: "Dinheiro" },
];

interface PaymentsFieldProps {
  payments: RentalPayment[];
  onChange: (payments: RentalPayment[]) => void;
  /** Rótulo do bloco — na edição admin vira "Novos pagamentos". */
  label?: string;
  /** Texto do estado vazio — na edição admin o contexto muda. */
  emptyMessage?: string;
}

export const PaymentsField = memo(function PaymentsField({
  payments,
  onChange,
  label = "Pagamentos",
  emptyMessage = "Nenhum pagamento registrado.",
}: PaymentsFieldProps) {
  const handleAdd = () => {
    onChange([...payments, { paymentMethod: "PIX", paidValue: 0 }]);
  };

  const handleRemove = (index: number) => {
    onChange(payments.filter((_, i) => i !== index));
  };

  const handleMethodChange = (index: number, method: RentalPaymentMethod) => {
    onChange(
      payments.map((p, i) =>
        i === index
          ? {
              ...p,
              paymentMethod: method,
              installments: supportsInstallments(method)
                ? (p.installments ?? MIN_INSTALLMENTS)
                : undefined,
            }
          : p,
      ),
    );
  };

  const handleInstallmentsChange = (index: number, installments: number) => {
    onChange(
      payments.map((p, i) => (i === index ? { ...p, installments } : p)),
    );
  };

  const handleValueChange = (index: number, value: number) => {
    onChange(
      payments.map((p, i) => (i === index ? { ...p, paidValue: value } : p)),
    );
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <Label>{label}</Label>
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={handleAdd}
          className="gap-2"
        >
          <Plus className="w-4 h-4" />
          Adicionar pagamento
        </Button>
      </div>

      {payments.length === 0 ? (
        <p className="text-xs text-muted-foreground">{emptyMessage}</p>
      ) : (
        <ul className="space-y-2">
          {payments.map((payment, index) => (
            <li
              key={index}
              className="grid grid-cols-1 md:grid-cols-[1fr_140px_150px_auto] gap-2 items-end rounded-md border border-input bg-background p-3"
            >
              <div className="space-y-1">
                <Label htmlFor={`payment-method-${index}`}>Método</Label>
                <select
                  id={`payment-method-${index}`}
                  value={payment.paymentMethod}
                  onChange={(e) =>
                    handleMethodChange(
                      index,
                      e.target.value as RentalPaymentMethod,
                    )
                  }
                  className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                >
                  {PAYMENT_METHOD_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1">
                <Label htmlFor={`payment-value-${index}`}>Valor (R$)</Label>
                <Input
                  id={`payment-value-${index}`}
                  type="number"
                  step="0.01"
                  min={0}
                  value={payment.paidValue || ""}
                  placeholder="0,00"
                  onChange={(e) =>
                    handleValueChange(index, Number(e.target.value) || 0)
                  }
                />
              </div>
              {supportsInstallments(payment.paymentMethod) ? (
                <div className="space-y-1">
                  <Label htmlFor={`payment-installments-${index}`}>
                    Parcelamento
                  </Label>
                  <select
                    id={`payment-installments-${index}`}
                    value={payment.installments ?? MIN_INSTALLMENTS}
                    onChange={(e) =>
                      handleInstallmentsChange(index, Number(e.target.value))
                    }
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm"
                  >
                    {INSTALLMENT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option === MIN_INSTALLMENTS ? "À vista" : `${option}x`}
                      </option>
                    ))}
                  </select>
                </div>
              ) : (
                <div className="hidden md:block" />
              )}
              <Button
                type="button"
                variant="ghost"
                size="icon-sm"
                onClick={() => handleRemove(index)}
                aria-label="Remover pagamento"
              >
                <Trash2 className="w-4 h-4" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
});
